import axios from "axios";
import { WIDGET_ENDPOINTS, API_BASE_URL } from "../constants/apis";
import tokenRefreshService from "./tokenRefresh";

// Document service for CRUD operations
class DocumentService {
    constructor() {
        this.baseURL = API_BASE_URL;
    }

    // Get all documents
    async getDocuments() {
        try {
            const token = await tokenRefreshService.getValidToken();
            const headers = {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            };

            const response = await axios.get(`${this.baseURL}${WIDGET_ENDPOINTS.DOCUMENTS_LIST}`, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching documents:', error);
            throw error;
        }
    }

    // Upload a document
    async uploadDocument(file) {
        try {
            const token = await tokenRefreshService.getValidToken();
            const formData = new FormData();
            formData.append('file', file);

            const config = {
                headers: {
                    'Authorization': `Bearer ${token}`
                    // Let the browser set Content-Type with boundary for multipart
                },
            };

            const response = await axios.post(`${this.baseURL}${WIDGET_ENDPOINTS.DOCUMENT_UPLOAD}`, formData, config);
            return response.data;
        } catch (error) {
            console.error('Error uploading document:', error);
            throw error;
        }
    }

    // Delete a document
    async deleteDocument(documentId) {
        try {
            const token = await tokenRefreshService.getValidToken();
            const headers = {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            };

            const response = await axios.delete(`${this.baseURL}${WIDGET_ENDPOINTS.DOCUMENT_DELETE(documentId)}`, { headers });
            return response.data;
        } catch (error) {
            console.error('Error deleting document:', error);
            throw error;
        }
    }

    // Get document details (if endpoint exists)
    async getDocument(documentId) {
        try {
            const token = await tokenRefreshService.getValidToken();
            const headers = {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            };

            const response = await axios.get(`${this.baseURL}${WIDGET_ENDPOINTS.DOCUMENT_DELETE(documentId)}`, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching document:', error);
            throw error;
        }
    }

    // Get raw document content by ID
    // Returns { document_name, content } from API
    async getDocumentContent(documentId) {
        try {
            const token = await tokenRefreshService.getValidToken();
            const headers = {
                'Authorization': `Bearer ${token}`
            };

            // API returns JSON with document_name and content fields
            const response = await axios.get(
                `${this.baseURL}${WIDGET_ENDPOINTS.DOCUMENT_CONTENT(documentId)}`,
                { headers, responseType: 'json' }
            );
            // Return the content and document name for easier use
            return {
                documentName: response.data.document_name || response.data.documentName,
                content: response.data.content || ''
            };
        } catch (error) {
            console.error('Error fetching document content:', error);
            throw error;
        }
    }

    // Edit/replace a document by ID (sends content as JSON)
    async editDocument(documentId, content) {
        try {
            if (!content) throw new Error('No content provided');

            const token = await tokenRefreshService.getValidToken();
            const config = {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            };

            const response = await axios.put(
                `${this.baseURL}${WIDGET_ENDPOINTS.DOCUMENT_EDIT(documentId)}`,
                { content },
                config
            );
            return response.data;
        } catch (error) {
            console.error('Error editing document:', error);
            throw error;
        }
    }

    // Validate file before upload
    validateFile(file) {
        const allowedTypes = [
            'text/plain',
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'text/markdown',
            'application/json'
        ];

        const maxSize = 10 * 1024 * 1024; // 10MB

        if (!file) {
            throw new Error('No file selected');
        }

        if (!allowedTypes.includes(file.type)) {
            throw new Error('File type not supported. Please upload PDF, DOC, DOCX, TXT, MD, or JSON files.');
        }

        if (file.size > maxSize) {
            throw new Error('File size too large. Maximum size is 10MB.');
        }

        return true;
    }

    // Format file size for display
    formatFileSize(bytes) {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    // Get file type icon
    getFileTypeIcon(fileType) {
        const iconMap = {
            'text/plain': '📄',
            'application/pdf': '📕',
            'application/msword': '📘',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '📘',
            'text/markdown': '📝',
            'application/json': '📋'
        };
        return iconMap[fileType] || '📄';
    }
}

// Create singleton instance
const documentService = new DocumentService();

export default documentService;
